import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { EmptyState, Loading } from "../../components/shared";
import { toast } from "sonner";
import { Plus, Wrench } from "lucide-react";

export default function VendorServices() {
  const [services, setServices] = useState(null);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/vendor/services").then((response) => setServices(response.data));
    api.get("/vendor/service-options").then((response) => setOptions(response.data));
  };

  useEffect(load, []);
  if (!services) return <Loading />;

  const addService = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post("/vendor/services", { service_id: selected });
      setSelected("");
      load();
      toast.success("Service added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add service");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (service) => {
    const status = service.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await api.patch(`/vendor/services/${service.vendor_service_id}`, { status });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update service");
    }
  };

  const mappedIds = new Set(services.map((item) => item.service_id));
  return (
    <section data-testid="vendor-services-page">
      <div className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Service Master</p>
        <h1 className="mt-2 font-display text-3xl font-bold">Services you provide</h1>
        <p className="mt-2 text-sm text-slate-600">Select approved MySocietyMitra services; service definitions remain managed centrally.</p>
      </div>
      <div className="mt-6 flex flex-col gap-3 border border-slate-200 bg-white p-5 sm:flex-row">
        <select
          className="h-10 min-w-0 flex-1 border border-slate-300 bg-white px-3 text-sm"
          data-testid="vendor-service-select"
          onChange={(event) => setSelected(event.target.value)}
          value={selected}
        >
          <option value="">Select a Service Master service</option>
          {options.filter((item) => !mappedIds.has(item.service_id)).map((item) => (
            <option key={item.service_id} value={item.service_id}>{item.category_name} · {item.name}</option>
          ))}
        </select>
        <Button data-testid="vendor-service-add-button" disabled={!selected || busy} onClick={addService} type="button">
          <Plus className="mr-2 h-4 w-4" />
          Add service
        </Button>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {services.length === 0 ? (
          <div className="sm:col-span-2 xl:col-span-3">
            <EmptyState icon={Wrench} subtitle="Add the services your business provides." title="No services selected" />
          </div>
        ) : services.map((item) => (
          <article className="border border-slate-200 bg-white p-5" data-testid={`vendor-service-${item.vendor_service_id}`} key={item.vendor_service_id}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.service.category_name}</p>
            <h2 className="mt-2 font-display text-lg font-bold">{item.service.name}</h2>
            <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <span className={`text-xs font-semibold ${item.status === "ACTIVE" ? "text-emerald-700" : "text-slate-500"}`}>
                {item.status.replaceAll("_", " ")}
              </span>
              <button
                className="text-sm font-semibold text-emerald-800 underline-offset-4 hover:underline"
                data-testid={`vendor-service-toggle-${item.vendor_service_id}`}
                onClick={() => toggle(item)}
                type="button"
              >
                {item.status === "ACTIVE" ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}